import { EditorContent } from '@tiptap/react';
import {
  animate,
  type AnimationPlaybackControls,
  motion,
  useMotionValue,
  useTransform,
} from 'motion/react';
import { useEffect, useRef } from 'react';

import { logChatRenderDebug } from '@/utils/chat-render-debug';
import { classList } from '@/utils/cn';
import {
  COMPOSER_TEXTAREA_MAX_HEIGHT,
  COMPOSER_TEXTAREA_MIN_HEIGHT,
} from '@/utils/constants';

import { ComposerEditorControls } from './composer-editor-controls';
import {
  type ComposerEditorOptions,
  useComposerEditor,
} from './useComposerEditor';

import '@/components/ui/editor/editor.css';

export type { ComposerEditorHandle } from './useComposerEditor';

export function ComposerEditor({
  placeholder,
  collapsed,
  findPortalContainer,
  ...editorOptions
}: ComposerEditorOptions & {
  placeholder?: string;
  collapsed?: boolean;
  findPortalContainer?: HTMLElement | null;
}) {
  const { value, disabled, fill, find } = editorOptions;
  const height = useMotionValue(COMPOSER_TEXTAREA_MIN_HEIGHT);
  const overflowY = useTransform(height, (value) =>
    value < COMPOSER_TEXTAREA_MAX_HEIGHT ? 'clip' : 'auto'
  );
  const heightControls = useRef<AnimationPlaybackControls | null>(null);

  const editor = useComposerEditor(editorOptions);
  logChatRenderDebug('composer-editor-render', {
    valueLength: value.length,
    hasDocument: editorOptions.document !== undefined,
    hasEditor: editor !== null,
  });

  useEffect(() => {
    if (!editor || collapsed || fill) return;
    const resize = () => {
      const target = Math.min(
        Math.max(editor.view.dom.offsetHeight, COMPOSER_TEXTAREA_MIN_HEIGHT),
        COMPOSER_TEXTAREA_MAX_HEIGHT
      );
      heightControls.current?.stop();
      heightControls.current = animate(height, target, {
        type: 'spring',
        stiffness: 800,
        damping: 35,
        mass: 0.1,
        onComplete: () => {
          if (target === COMPOSER_TEXTAREA_MAX_HEIGHT)
            requestAnimationFrame(() => {
              if (
                !editor.isDestroyed &&
                editor.isFocused &&
                height.get() >= target
              )
                editor.commands.scrollIntoView();
            });
        },
      });
    };
    const observer = new ResizeObserver(resize);
    observer.observe(editor.view.dom, { box: 'border-box' });
    resize();
    return () => {
      observer.disconnect();
      heightControls.current?.stop();
    };
  }, [editor, collapsed, fill, height]);

  const placeholderText = placeholder || 'Ask Littlebird';

  return (
    <motion.div
      className={classList(
        'relative w-full text-foreground transition-opacity',
        fill
          ? 'h-full pl-3 pr-8 leading-5 [&>div]:h-full [&_.ProseMirror]:min-h-full'
          : 'px-2',
        !fill &&
          (collapsed
            ? 'h-9 overflow-hidden leading-8 [&_p]:whitespace-nowrap'
            : 'mt-2 overflow-y-auto [&_.ProseMirror:has(>_:nth-child(2))]:pb-[calc(1lh+0.125rem)]'),
        disabled && 'opacity-60'
      )}
      style={
        collapsed || fill
          ? undefined
          : {
              height,
              overflowY,
              minHeight: `${COMPOSER_TEXTAREA_MIN_HEIGHT}px`,
              maxHeight: `${COMPOSER_TEXTAREA_MAX_HEIGHT}px`,
            }
      }
    >
      {value.length === 0 && (
        <span
          className={classList(
            'pointer-events-none absolute select-none text-foreground-muted',
            fill
              ? 'left-3 top-3'
              : collapsed
                ? 'top-1/2 -translate-y-1/2'
                : 'top-0.5'
          )}
        >
          {placeholderText}
        </span>
      )}
      <ComposerEditorControls
        editor={editor}
        find={find}
        findPortalContainer={findPortalContainer}
      >
        <EditorContent
          editor={editor}
          className={fill ? 'h-full' : undefined}
        />
      </ComposerEditorControls>
    </motion.div>
  );
}
